import React, { useState, useEffect, useMemo, useRef } from 'react';
// Usar el mismo módulo de estilos de Programas para replicar diseño
import styles from '../../styles/PantallaDeProgramasAcademicos.v2.module.css';

import StatusBadge from './StatusBadge';
import ActionButtons from './ActionButtons';
import { getCycles } from '../../api/ciclosEscolares';
import Pagination from '../../components/Pagination.tsx';

const gridCols = '1fr 1.5fr 1.2fr 1.2fr 1.2fr 1.2fr 1fr 1fr';


const CiclosTable = ({ refreshTrigger, onView, onEdit, onDelete }) => {
  const [ciclosData, setCiclosData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const pageSize = 8;

  useEffect(() => {
    const fetchCiclos = async () => {
      try {
        setLoading(true);
        const data = await getCycles();
        setCiclosData(data);
      } catch (error) {
        console.error('Error al cargar ciclos escolares:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCiclos();
  }, [refreshTrigger]);

  // Crear clave única basada en IDs para detectar cambios reales
  const ciclosKey = useMemo(() => {
    return ciclosData.map(c => c.id_ciclo_escolar || c.id).sort().join(',');
  }, [ciclosData]);

  const prevCiclosKeyRef = useRef(ciclosKey);

  // Reiniciar página solo cuando realmente cambia la lista
  useEffect(() => {
    if (prevCiclosKeyRef.current !== ciclosKey) {
      setPage(1);
      prevCiclosKeyRef.current = ciclosKey;
    }
  }, [ciclosKey]);

  const totalPages = Math.max(1, Math.ceil(ciclosData.length / pageSize));

  // Ajustar página si quedó fuera de rango
  useEffect(() => {
    if (page > totalPages && totalPages > 0) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const paginated = ciclosData.slice((page - 1) * pageSize, page * pageSize);
  // Eliminar goToPage, usaremos Pagination global

  // Convertir fecha ISO a formato DD/MM/YYYY
  const formatDate = (isoDate) => {
    if (!isoDate) return '';
    const date = new Date(isoDate);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Mapear estado_ciclo a formato del badge
  const mapEstadoCiclo = (estadoCiclo) => {
    if (!estadoCiclo) return 'planeado';
    return estadoCiclo.toLowerCase();
  };

  const Header = () => (
    <header className={styles.tableHeader}>
      <div className={styles.tableTitle}>
        <div className={styles.tableTitleIcon}>
          <div className={styles.iconContainerOrange}><span>🔄</span></div>
        </div>
        <h2 className={styles.tableTitleText}>Ciclos Escolares</h2>
      </div>
    </header>
  );

  if (loading) {
    return (
      <section className={styles.tableContainer} role="region" aria-label="Tabla de ciclos escolares">
        <Header />
        <div style={{ padding: '20px', textAlign: 'center' }}>Cargando ciclos...</div>
      </section>
    );
  }

  return (
    <section className={styles.tableContainer} role="region" aria-label="Tabla de ciclos escolares">
      <Header />
      {/* Cabecera de columnas igual a la imagen */}
      <div className={styles.tableHeaderRow} style={{ gridTemplateColumns: gridCols, columnGap: '24px', display: 'grid' }}>
        <span className={styles.columnHeader}>Año</span>
        <span className={styles.columnHeader}>Periodo</span>
        <span className={styles.columnHeader}>Fecha Inicio</span>
        <span className={styles.columnHeader}>Fecha Fin</span>
        <span className={styles.columnHeader}>Inscripción</span>
        <span className={styles.columnHeader}>Reinscripción</span>
        <span className={styles.columnHeader}>Estado</span>
        <span className={styles.columnHeaderWide}>Acciones</span>
      </div>

      <div className={styles.tableBody}>
        <>
          {paginated.map((c) => (
            <div key={c.id} className={styles.tableRow} style={{ gridTemplateColumns: gridCols, columnGap: '24px', display: 'grid' }}>
              <span className={styles.cellData}>{c.anio}</span>
              <span className={styles.cellData}>
                {c.numero_periodo} ({c.tipo_periodo_nombre || 'Periodo'})
              </span>
              <span className={styles.cellData}>{formatDate(c.fecha_inicio)}</span>
              <span className={styles.cellData}>{formatDate(c.fecha_fin)}</span>
              <span className={styles.cellData}>{formatDate(c.fecha_limite_inscripciones)}</span>
              <span className={styles.cellData}>{formatDate(c.fecha_limite_reinscripciones)}</span>
              <div className={styles.statusCell}>
                <StatusBadge status={mapEstadoCiclo(c.estado_ciclo)} />
              </div>
              <ActionButtons 
                onView={() => onView(c)}
                onEdit={() => onEdit(c)}
                onDelete={() => onDelete(c)}
              />
            </div>
          ))}
          {ciclosData.length === 0 && (
            <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '20px', color: '#666' }}>
              No hay ciclos escolares registrados
            </div>
          )}
        </>
      </div>
      {/* Paginación global */}
      {ciclosData.length > pageSize && (
        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          className={styles.paginationWrapper}
        />
      )}
    </section>
  );
};

export default CiclosTable;
